import { useEffect, useState } from 'react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

/**
 * Same rule as catalog edit / TechDocs shadow edit: ADMIN_GROUP or direct GitLab
 * project member with Developer+ (30). Does NOT include view-source-only group
 * membership (see catalog-edit-check).
 *
 * When `entityRefStr` changes, `allowed` resets to `null` during render so callers
 * never briefly treat the user as privileged using the previous entity's result
 * (fixes TechDocs Refresh / build logs flashing for normal users after navigation).
 */
export function useCatalogEditAllowed(entityRefStr: string): boolean | null {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [trackedEntityRef, setTrackedEntityRef] = useState(entityRefStr);

  if (entityRefStr !== trackedEntityRef) {
    setTrackedEntityRef(entityRefStr);
    setAllowed(null);
  }

  useEffect(() => {
    const ref = entityRefStr.trim();
    if (!ref) {
      setAllowed(false);
      return undefined;
    }
    let cancelled = false;
    const backend = config.getString('backend.baseUrl').replace(/\/$/, '');
    const q = encodeURIComponent(ref);
    const url = `${backend}/api/catalog-edit-check?entityRef=${q}&check=edit&_=${Date.now()}`;
    fetchApi
      .fetch(url, { credentials: 'include', cache: 'no-store' })
      .then(async res => {
        if (res.status === 304 || res.status === 204) return { allowed: false };
        if (!res.ok) return { allowed: false };
        try {
          return await res.json();
        } catch {
          return { allowed: false };
        }
      })
      .then((body: { allowed?: boolean }) => {
        if (!cancelled) setAllowed(Boolean(body?.allowed));
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityRefStr, fetchApi, config]);

  return allowed;
}
